
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { toast } from 'sonner';
import { CreateEstimateStratigraphyData } from '@/types/estimateStratigraphy';
import { fetchFinishCostSnapshot } from '@/utils/estimates/fetchFinishCostSnapshot';
import { finishedFacesFor } from '@/components/configurator-v2/hooks/useTypologyDetection';
import { useOrgProfile } from '@/hooks/useOrgSettings';
import { useWasteFactors } from '@/hooks/useWasteFactors';
import { mergeEstimatePricing } from '@/utils/pricing/mergeEstimatePricing';
import { rowCostOptionsFrom, wasteAndDisposalPerSqm } from '@/utils/pricing/computeEstimateCostBase';

export const useCreateEstimateStratigraphy = () => {
  const { user } = useAuth();
  const { currentOrganizationId } = useCurrentOrganization();
  const queryClient = useQueryClient();
  // Servono a includere sfrido e smaltimento nel costo salvato della riga.
  const { data: orgProfile } = useOrgProfile();
  const { wasteMap } = useWasteFactors();

  const createEstimateStratigraphyMutation = useMutation({
    mutationFn: async (data: CreateEstimateStratigraphyData) => {
      if (!user) throw new Error('User not authenticated');

      // PROTEZIONE: Verifica lo stato del preventivo prima di procedere
      const { data: estimateData, error: estimateError } = await supabase
        .from('estimates')
        .select('status, project_id, pricing_overrides')
        .eq('id', data.estimateId)
        .single();

      if (estimateError) {
        console.error('Error fetching estimate for validation:', estimateError);
        throw new Error('Preventivo non trovato');
      }

      // F30: blocca se il preventivo è vinto/perso (o legacy contracted).
      if (estimateData.status === 'won' || estimateData.status === 'lost' || estimateData.status === 'contracted') {
        const label = estimateData.status === 'lost' ? 'perso' : 'vinto/contrattualizzato';
        throw new Error(`Impossibile aggiungere stratigrafie: preventivo ${label}`);
      }

      // Accessibilita' progetto via RLS org-scoped (is_member_of): niente filtro
      // user_id, altrimenti un membro del team non puo' aggiungere stratigrafie
      // ai preventivi di progetti creati da altri.
      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .select('id')
        .eq('id', estimateData.project_id)
        .single();

      if (projectError || !projectData) {
        throw new Error('Accesso negato al progetto');
      }

      // First, get the complete stratigraphy data with layers and materials (including custom_screws)
      const { data: stratigraphySnapshot, error: snapshotError } = await supabase
        .from('stratigraphies')
        .select(`
          *,
          layers (
            id,
            position,
            thickness,
            inter_axis,
            material_id,
            screw_material_id,
            anchor_material_id,
            screw_quantity,
            screw_cost_per_sqm,
            material_cost_per_sqm,
            installation_time_minutes,
            material_quantity_per_sqm,
            materials!layers_material_id_fkey (
              id,
              name,
              category,
              unit,
              unit_price,
              incidence_per_sqm,
              color_hex,
              supplier,
              code,
              description,
              width,
              weight_per_sqm,
              thermal_conductivity,
              acoustic_performance,
              fire_resistance_class,
              compatible_board_types,
              length,
              box_pieces,
              installation_time_per_sqm,
              waste_percentage
            ),
            screw_materials:materials!layers_screw_material_id_fkey (
              id,
              name,
              category,
              unit,
              unit_price,
              incidence_per_sqm,
              color_hex,
              supplier,
              code,
              description,
              length,
              box_pieces,
              installation_time_per_sqm,
              waste_percentage
            ),
            anchor_materials:materials!layers_anchor_material_id_fkey (
              id,
              name,
              category,
              unit,
              unit_price,
              incidence_per_sqm,
              color_hex,
              supplier,
              code,
              description,
              length,
              box_pieces,
              installation_time_per_sqm,
              waste_percentage
            )
          )
        `)
        .eq('id', data.stratigraphyId)
        .single();

      if (snapshotError) {
        console.error('Error fetching stratigraphy snapshot:', snapshotError);
        throw snapshotError;
      }

      // F7.7: calcola costo finitura se livello specificato + org disponibile
      let finishSnapshot: Awaited<ReturnType<typeof fetchFinishCostSnapshot>> = null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const finishLevelCode = (data as any).finishLevel as string | undefined;
      if (finishLevelCode && currentOrganizationId) {
        // Le facce da finire decidono se la finitura vale intera (divisoria) o
        // meta' (controparete/controsoffitto): i valori Q1-Q4 in Impostazioni
        // sono riferiti a una parete finita su entrambi i lati.
        const finishedFaces = finishedFacesFor((stratigraphySnapshot as any)?.layers);
        finishSnapshot = await fetchFinishCostSnapshot(
          currentOrganizationId,
          finishLevelCode,
          finishedFaces,
        );
      }

      // F19.7 (2026-05-25) — Ricalcolo LIVE del prezzo stratigrafia al momento
      // dell'aggiunta. Prima usavamo `data.unitCost` (snapshot stale di
      // comprehensive_cost_per_sqm) → il primo "Aggiorna prezzi" produceva
      // sempre un delta perché il bulk update ricalcola con prezzi netti
      // correnti da `materials_with_pricing`. Ora replichiamo qui la stessa
      // formula così Add e Update sono SEMPRE coerenti.
      //
      // Bug ref: cliente con sconti SG significativi (~60%), parete DA 75/50
      // LA34 GX: snapshot €73.71 → bulk update €69.97. Delta €3.74/m².
      let recomputedUnitCost: number | null = null;
      // Non arrotondato: e` la base con cui il bulk update calcola il totale.
      let recomputedUnitCostExact: number | null = null;
      // I sei campi di sintesi della fotografia, ricalcolati. Restano null se il
      // ricalcolo non riesce: in quel caso si salva la fotografia grezza, come
      // prima. Vedi il commento all'INSERT.
      let recomputedSnapshotFields: Record<string, number> | null = null;
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const dbLayers: any[] = (stratigraphySnapshot as any).layers ?? [];
        if (dbLayers.length > 0) {
          // Override unit_price coi NETTI da materials_with_pricing (RLS-aware)
          const layerMatIds: string[] = [];
          for (const dbL of dbLayers) {
            if (dbL.materials?.id) layerMatIds.push(dbL.materials.id);
            if (dbL.screw_materials?.id) layerMatIds.push(dbL.screw_materials.id);
            if (dbL.anchor_materials?.id) layerMatIds.push(dbL.anchor_materials.id);
          }
          const uniqueLayerMatIds = Array.from(new Set(layerMatIds));
          if (uniqueLayerMatIds.length > 0) {
            const { data: pricing } = await supabase
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .from('materials_with_pricing' as any)
              // Sfrido e smaltimento arrivano dalla vista gia` RISOLTI per
              // l'organizzazione (COALESCE con l'override per-org): il join FE
              // su `materials` porterebbe i valori grezzi di catalogo, e su un
              // materiale globale l'override non arriverebbe mai alla riga.
              .select('id, unit_price, waste_percentage, disposal_percentage')
              .in('id', uniqueLayerMatIds);
            const priceMap = new Map<string, number>();
            const effMap = new Map<string, { waste: number | null; disposal: number | null }>();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            for (const p of (pricing ?? []) as any[]) {
              priceMap.set(p.id, Number(p.unit_price ?? 0));
              effMap.set(p.id, {
                waste: p.waste_percentage != null ? Number(p.waste_percentage) : null,
                disposal: p.disposal_percentage != null ? Number(p.disposal_percentage) : null,
              });
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const applica = (mat: any) => {
              const id = mat?.id;
              if (!id) return;
              if (priceMap.has(id)) mat.unit_price = priceMap.get(id);
              const eff = effMap.get(id);
              if (eff) {
                mat.waste_percentage = eff.waste;
                mat.disposal_percentage = eff.disposal;
              }
            };
            for (const dbL of dbLayers) {
              applica(dbL.materials);
              applica(dbL.screw_materials);
              applica(dbL.anchor_materials);
            }
          }

          // Costo orario corrente (stessa fonte del bulk update)
          const { data: settingData } = await supabase
            .from('configurator_settings')
            .select('value')
            .eq('key', 'cost_per_hour')
            .maybeSingle();
          const costPerHour = parseFloat(settingData?.value ?? '30') || 30;

          // Costruisci LayerV2[] come nel bulk update
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const sortedDbLayers = [...dbLayers].sort((a: any, b: any) => a.position - b.position);
          const layersForCompute = sortedDbLayers.map((dbL, idx) => ({
            id: dbL.id ?? `tmp-${idx}`,
            position: idx + 1,
            materialId: dbL.material_id ?? null,
            material: dbL.materials ?? null,
            thickness: Number(dbL.thickness ?? 0),
            interAxis: dbL.inter_axis ?? undefined,
            screwMaterialId: dbL.screw_material_id ?? null,
            screwMaterial: dbL.screw_materials ?? null,
            anchorMaterialId: dbL.anchor_material_id ?? null,
            anchorMaterial: dbL.anchor_materials ?? null,
            screwQuantity: dbL.screw_quantity ?? undefined,
            screwCostPerSqm: dbL.screw_cost_per_sqm ?? undefined,
            // Consumo dichiarato sul layer. Senza questa riga il motore ricade
            // sull'incidenza di catalogo e il preventivo valorizza una quantita`
            // diversa da quella che la RDA ordina: si vendono 2 kg e se ne
            // comprano 3.
            materialQuantity: dbL.material_quantity_per_sqm ?? null,
          }));

          // Import dinamico per evitare loop circolari + bundle bloat
          const { computeStratigraphyCosts } = await import('@/components/configurator-v2/hooks/computeStratigraphyCosts');
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const studSpacingMm = Number((stratigraphySnapshot as any).stud_spacing_mm ?? 600);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const breakdown = computeStratigraphyCosts(layersForCompute as any, studSpacingMm, costPerHour);
          if (breakdown.totalCost > 0) {
            recomputedUnitCost = Math.round(breakdown.totalCost * 100) / 100;
            recomputedUnitCostExact = breakdown.totalCost;
            // STESSI campi e STESSI arrotondamenti di
            // useBulkUpdateEstimateStratigraphyPrices: e` proprio la differenza
            // fra questi due punti a far "saltare" costo manodopera e durata al
            // primo Aggiorna Prezzi.
            recomputedSnapshotFields = {
              material_cost_per_sqm: Math.round(breakdown.subtotalMaterials * 1000) / 1000,
              screw_cost_per_sqm: Math.round(breakdown.subtotalScrews * 1000) / 1000,
              labor_cost_per_sqm: Math.round(breakdown.laborCost * 1000) / 1000,
              installation_time_per_sqm: Math.round(breakdown.laborMinutes * 1000) / 1000,
              comprehensive_cost_per_sqm: recomputedUnitCost,
              cost_per_sqm: recomputedUnitCost,
            };
          }
        }
      } catch (err) {
        console.warn('[useCreateEstimateStratigraphy] Live recompute failed, fallback to snapshot:', err);
      }

      // Sommiamo il finish_cost al unit_cost: il preventivo è "tutto incluso"
      // (parete + finitura). L'utente vede un €/m² unico.
      // `unit_cost` va arrotondato ai centesimi perche' e` un prezzo al m²
      // mostrato a video, ma il TOTALE si calcola sul valore ESATTO — come fa
      // il bulk update. Il commento precedente diceva "identico al bulk update"
      // ma faceva l'opposto: moltiplicava per il valore arrotondato. Da li` gli
      // 8 centesimi di scarto fra inserimento e primo Aggiorna Prezzi.
      const finishCostPerSqm = finishSnapshot?.finishCostPerSqm ?? 0;
      // Usa il valore ricalcolato live (allineato col bulk update) se disponibile,
      // altrimenti fallback al snapshot stale (UX degradata ma niente blocchi).
      // SFRIDO E SMALTIMENTO nel costo salvato. Stessa funzione che alimenta il
      // prezzo di vendita, quindi vale `area x unit_cost = costBase`: senza,
      // la colonna Totale mostrerebbe un costo e il prezzo ne userebbe un altro
      // — che e` esattamente il disallineamento segnalato dall'utente.
      const costOpts = rowCostOptionsFrom(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        mergeEstimatePricing(orgProfile ?? null, (estimateData as any)?.pricing_overrides),
        wasteMap,
      );
      // I layer sono gli stessi oggetti su cui l'overlay ha gia` scritto
      // sfrido e smaltimento EFFETTIVI per l'organizzazione.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const extra = wasteAndDisposalPerSqm((stratigraphySnapshot as any)?.layers, costOpts);
      const unitCostExact = (recomputedUnitCostExact ?? Number(data.unitCost))
        + finishCostPerSqm + extra.waste + extra.disposal;
      const finalUnitCost = Math.round(unitCostExact * 100) / 100;
      // F17.2.13: il profilo perimetrale dei controsoffitti è una voce fissa per
      // stanza (€ totali), si somma una volta sola al totale (non moltiplicato per area).
      const perimeterCostAdd = Number(data.perimeterProfileCost ?? 0);
      const totalCost = Math.round(
        (data.area * (data.quantity || 1) * unitCostExact + perimeterCostAdd) * 100,
      ) / 100;

      const { data: result, error } = await supabase
        .from('estimate_stratigraphies')
        .insert({
          estimate_id: data.estimateId,
          stratigraphy_id: data.stratigraphyId,
          name: data.name,
          description: data.description,
          area: data.area,
          quantity: data.quantity || 1,
          unit_cost: finalUnitCost,
          total_cost: totalCost,
          // F32: persistiamo l'altezza parete dichiarata nel dialog. Serve
          // a useMaterialsSummary per il calcolo delle guide e (in futuro)
          // per il warning "altezza > lunghezza foglio = giunto orizzontale".
          // Bug pre-F32: il valore veniva raccolto ma mai scritto su DB.
          wall_height: data.wallHeight ?? null,
          // F7.7: livello + costo + snapshot BOM (per recalcolo prezzi futuro)
          finish_level: finishLevelCode ?? null,
          finish_cost_per_sqm: finishCostPerSqm,
          finish_labor_minutes_per_sqm: finishSnapshot?.finishLaborMinPerSqm ?? null,
          finish_components_data: finishSnapshot?.finishComponentsSnapshot ?? null,
          // New snapshot fields including custom_screws data
          //
          // La fotografia porta i valori RICALCOLATI, non quelli grezzi di
          // catalogo. La tabella del preventivo legge da qui
          // `installation_time_per_sqm` per costruire Costo Manodopera, Costo
          // Materiale (che e` un residuo: unit_cost meno manodopera) e Tempo.
          // Salvandola grezza, quelle tre colonne cambiavano al primo Aggiorna
          // Prezzi senza che nessun costo fosse cambiato davvero — ed erano gli
          // stessi campi da cui nasce il prezzo di vendita.
          stratigraphy_data: recomputedSnapshotFields
            ? { ...stratigraphySnapshot, ...recomputedSnapshotFields }
            : stratigraphySnapshot,
          layers_data: (stratigraphySnapshot.layers ?? []).slice().sort(
            (a: { position: number }, b: { position: number }) => a.position - b.position,
          ),
          prices_updated_at: new Date().toISOString(),
          is_snapshot: true,
          original_stratigraphy_id: data.stratigraphyId,
          // F17.2.13: perimetro stanza + costo profilo perimetrale per i controsoffitti.
          // Per le pareti restano null (le colonne esistono già nel DB).
          perimeter_m: data.perimeterM ?? null,
          perimeter_profile_cost: data.perimeterProfileCost ?? null,
          // F17 post-2026-05-22: FK al material del profilo perimetrale. Permette
          // a useMaterialsSummary di aggregarlo nel riepilogo materiali (RDA) e a
          // useBulkUpdateEstimateStratigraphyPrices di ricalcolare il costo dal
          // prezzo aggiornato live del materiale.
          perimeter_profile_id: data.perimeterProfileId ?? null,
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating estimate stratigraphy:', error);
        throw error;
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimate-stratigraphies'] });
      queryClient.invalidateQueries({ queryKey: ['estimate'] });
      toast.success('Stratigrafia aggiunta al preventivo!');
    },
    onError: (error) => {
      console.error('Error creating estimate stratigraphy:', error);
      if (error.message.includes('contrattualizzato')) {
        toast.error('Impossibile aggiungere: preventivo contrattualizzato');
      } else {
        toast.error('Errore nell\'aggiunta della stratigrafia');
      }
    },
  });

  return {
    createEstimateStratigraphy: createEstimateStratigraphyMutation.mutate,
    isCreating: createEstimateStratigraphyMutation.isPending,
  };
};
